import { useEffect, useMemo, useRef, HTMLAttributes } from 'react'
import { useSpring, config } from '@react-spring/web'
import { useDispatch } from 'react-redux'
import Logo from 'builder/components/DocumentEditor/JobPosting/Images/CompanyLogo.svg'
import { actions, selectors } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import type { ToneName } from 'builder/styles/colors'
import { FetchStatuses } from 'builder/modules/constants'
import { useTailoringBannerTriggerConfig } from 'builder/hooks/useTailoringBannerTriggerConfig'
import { CompanyLogo } from '../DocumentEditor/JobPosting/DataContainer/Views/Result/styles'
import ImproveResumePanel from '../ImproveResumePanel/ImproveResumePanel'
import ImproveResumeButton from '../ImproveResumePanel/components/ImproveResumeButton/ImproveResumeButton'
import useImproveResumePanel from '../ImproveResumePanel/hooks/useImproveResumePanel'
import { TailorResumeBadge } from '../ImproveResumePanel'
import {
  Container,
  Header,
  HeaderMain,
  HeaderAside,
  Percent,
  Progress,
  ProgressInner,
  HeaderText,
} from './styles'
import EditView from './JobTitleSection/EditView'

export const ResumeScore = (props: HTMLAttributes<HTMLDivElement>) => {
  const isImproveResumePanelOpen = useTypedSelector(selectors.isImproveResumePanelOpen)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const improveResumeButtonRef = useRef<HTMLDivElement>(null)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const resumeOptimizerData = useTypedSelector(selectors.jobPostingAPIData)
  const {
    isPageScrolled,
    isResumeOptimizerFeatureEnabled,
    isAICoverLetterActive,
    isAIProfileWriterActive,
  } = useImproveResumePanel()
  const { isBannerTriggerAvailable } = useTailoringBannerTriggerConfig()

  let tailoredJobTitle = ''
  const { recommendedJobTitle, employerName } = resumeOptimizerData
  const resumeScore = useTypedSelector(selectors.resumeScore) || 0

  const showImproveResumePanel =
    isResumeOptimizerFeatureEnabled ||
    isAICoverLetterActive ||
    isAIProfileWriterActive ||
    resumeScore < 100

  const points = resumeOptimizerData.averageScore || resumeScore
  tailoredJobTitle =
    employerName !== 'n'
      ? [recommendedJobTitle, employerName].join(
          ' ' + i18n.t('builder.resume_editor.labels.at') + ' ',
        )
      : recommendedJobTitle || ''

  const editJobPosting = useTypedSelector(state => state.resumeEditor.editJobPosting)
  const isEditJobDetails = useTypedSelector(state => state.resumeEditor.isEditJobDetails)

  useEffect(() => {
    if (editJobPosting) {
      dispatch(actions.setFetchResumeScoreStatus(FetchStatuses.notAsked))
      dispatch(actions.setSaveJobPostingDataStatus(FetchStatuses.notAsked))
      dispatch(actions.setIsEditJobPosting(false))
    }
  }, [dispatch, editJobPosting])

  const [barColor] = useMemo(() => {
    const getColorByPoints = (points: number): ToneName => {
      if (points > 70) return 'green'
      if (points > 30) return 'amber'
      return 'red'
    }

    const color = getColorByPoints(points)
    return [`var(--${color}-40)`, `var(--${color}-50)`]
  }, [points])

  const springProps = useSpring({
    config: config.slow,
    from: { points: 0 },
    to: { points: Math.min(points, 100) },
  })

  const isJobpostingData = recommendedJobTitle

  return (
    <Container ref={containerRef} isPageScrolled={isPageScrolled} {...props}>
      <Header>
        <HeaderMain>
          <Percent points={points}>{springProps.points.to(value => value.toFixed(0))}</Percent>
          {isJobpostingData ? (
            <>
              <CompanyLogo style={{ marginRight: '8px' }} size={20} src={Logo} />
              <EditView tailoredJobTitle={tailoredJobTitle} />
            </>
          ) : (
            <HeaderText>{i18n.t('builder.resume_tuner.banner.dashboard_score')}</HeaderText>
          )}
        </HeaderMain>
        {!isEditJobDetails &&
          (showImproveResumePanel ? (
            <HeaderAside>
              {isBannerTriggerAvailable && <TailorResumeBadge />}
              <ImproveResumeButton ref={improveResumeButtonRef} />
            </HeaderAside>
          ) : null)}
      </Header>

      <Progress>
        <ProgressInner
          style={{
            transform: springProps.points.to(value => `scaleX(${value / 100})`),
            backgroundColor: barColor,
          }}
        />
      </Progress>

      {showImproveResumePanel && isImproveResumePanelOpen && (
        <ImproveResumePanel improveResumeButtonRef={improveResumeButtonRef} />
      )}
    </Container>
  )
}
